import { ORDER } from '../constants/game';
import { colorOf } from './helpers';
import SND from './sound';

const SIZE = 600;
const DPR = Math.min(window.devicePixelRatio || 1, 2);
const STEP = (Math.PI * 2) / 38;

export function setupCanvas(cv) {
  cv.width = cv.height = SIZE * DPR;
  const g = cv.getContext('2d');
  g.scale(DPR, DPR);
  g.translate(SIZE / 2, SIZE / 2);
  return g;
}

const disc = (g, r, fill) => { g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.fillStyle = fill; g.fill(); };
const ring = (g, r, col, w) => { g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.strokeStyle = col; g.lineWidth = w; g.stroke(); };

export function drawWheel(g) {
  let gr = g.createRadialGradient(0, 0, 220, 0, 0, 295);
  gr.addColorStop(0, '#8a1c0e'); gr.addColorStop(0.6, '#6a0e06'); gr.addColorStop(1, '#3b0703');
  disc(g, 295, gr); ring(g, 289, 'rgba(255,200,120,.35)', 2);
  gr = g.createRadialGradient(0, 0, 214, 0, 0, 262);
  gr.addColorStop(0, '#b9822b'); gr.addColorStop(0.5, '#f3c968'); gr.addColorStop(1, '#c48a30');
  disc(g, 262, gr); ring(g, 262, '#5a2a05', 3); ring(g, 240, 'rgba(120,70,10,.7)', 1.5);
  for (let i = 0; i < 8; i++) {
    g.save(); g.rotate((i * Math.PI) / 4); g.translate(0, -238);
    g.beginPath(); g.moveTo(0, -12); g.lineTo(6, 0); g.lineTo(0, 12); g.lineTo(-6, 0); g.closePath();
    g.fillStyle = '#fff1b8'; g.globalAlpha = 0.75; g.fill(); g.restore();
  }
  disc(g, 214, '#111');
  const cols = { red: '#c4161f', black: '#121212', green: '#1d9a35' };
  ORDER.forEach((n, i) => {
    const a0 = i * STEP - STEP / 2 - Math.PI / 2, a1 = a0 + STEP;
    g.beginPath(); g.arc(0, 0, 207, a0, a1); g.arc(0, 0, 140, a1, a0, true); g.closePath();
    g.fillStyle = cols[colorOf(n)]; g.fill();
    g.strokeStyle = '#d9b45a'; g.lineWidth = 1.5; g.stroke();
    g.save(); g.rotate(i * STEP); g.translate(0, -191);
    g.fillStyle = '#fff'; g.font = 'bold 17px Georgia, serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(String(n), 0, 0); g.restore();
  });
  ring(g, 174, '#d9b45a', 2); ring(g, 207, '#d9b45a', 2.5);
  gr = g.createRadialGradient(0, 0, 10, 0, 0, 140);
  gr.addColorStop(0, '#d1381f'); gr.addColorStop(0.6, '#9a1608'); gr.addColorStop(1, '#5a0b05');
  disc(g, 140, gr); ring(g, 138, '#e8b552', 4);
  for (let i = 0; i < 4; i++) {
    g.save(); g.rotate((i * Math.PI) / 2);
    const lg = g.createLinearGradient(-6, 0, 6, 0);
    lg.addColorStop(0, '#a8741f'); lg.addColorStop(0.5, '#ffe08a'); lg.addColorStop(1, '#a8741f');
    g.fillStyle = lg; g.fillRect(-5, -95, 10, 95);
    g.beginPath(); g.arc(0, -96, 8, 0, Math.PI * 2); g.fillStyle = '#f3c968'; g.fill(); g.restore();
  }
  gr = g.createRadialGradient(-6, -6, 2, 0, 0, 26);
  gr.addColorStop(0, '#fff1b8'); gr.addColorStop(1, '#a8741f');
  disc(g, 26, gr); disc(g, 11, '#e8b552');
}

function drawBall(bg, ang, r) {
  bg.clearRect(-SIZE / 2, -SIZE / 2, SIZE, SIZE);
  const x = Math.cos(ang) * r, y = Math.sin(ang) * r;
  bg.save();
  bg.shadowColor = 'rgba(0,0,0,.6)'; bg.shadowBlur = 6; bg.shadowOffsetY = 3;
  const g = bg.createRadialGradient(x - 2, y - 2, 1, x, y, 8);
  g.addColorStop(0, '#fff'); g.addColorStop(1, '#bbb');
  bg.beginPath(); bg.arc(x, y, 7.5, 0, Math.PI * 2); bg.fillStyle = g; bg.fill();
  bg.restore();
}

const ease = (q) => q * q * (3 - 2 * q);

// Wheel + ball animation. Returns a cancel function.
export function runSpin({ wheelEl, ballCtx, win, fast, onLand, onDone }) {
  const pocket = ORDER.findIndex((x) => String(x) === String(win)) * STEP - Math.PI / 2;
  const T = fast ? 3200 : 6200, HOLD = 2800, Ts = T / 1000;
  const th0 = Math.random() * Math.PI * 2, w0 = fast ? 4.2 : 3.0, N = fast ? 5 : 8;
  let landed = false, lastN = null, cancelled = false, raf = 0;
  const t0 = performance.now();

  function frame(now) {
    if (cancelled) return;
    const t = now - t0, ts = t / 1000, tt = Math.min(ts, Ts);
    const th = th0 + w0 * (tt - (0.4 * tt * tt) / Ts) + (ts > Ts ? 0.2 * w0 * (ts - Ts) : 0);
    wheelEl.style.transform = `rotate(${th}rad)`;
    const p = Math.min(t / T, 1), rel = Math.PI * 2 * N * (1 - p) ** 2;
    let r = 232;
    if (p >= 0.6 && p < 0.82) r = 232 - 44 * ease((p - 0.6) / 0.22);
    else if (p >= 0.82) {
      const q = (p - 0.82) / 0.18;
      r = 188 - 30 * ease(q) + Math.sin(q * Math.PI * 3) * 5 * (1 - q);
    }
    drawBall(ballCtx, th + pocket + rel, r);
    if (!landed && p > 0.55) {
      const n = Math.floor((pocket + rel + Math.PI / 2) / STEP + 0.5);
      if (n !== lastN) { if (lastN !== null) SND.tick(p); lastN = n; }
      if (p > 0.82 && p < 0.97 && Math.random() < 0.06) SND.clack();
    }
    if (t >= T && !landed) { landed = true; SND.rollStop(); SND.land(); onLand(); }
    if (t >= T + HOLD) { onDone(); return; }
    raf = requestAnimationFrame(frame);
  }
  frame(t0);
  return () => { cancelled = true; cancelAnimationFrame(raf); };
}
